/**
 * Cloudinary Storage Service
 * 
 * Utilities for uploading and managing images via Cloudinary.
 * Replaces Firebase Storage for cost efficiency.
 */

import cloudinaryConfig, { validateCloudinaryConfig } from '../config/cloudinary'

/**
 * Generate a unique filename with timestamp
 * @param {string} originalName - The original filename
 * @returns {string} - Unique filename
 */
export function generateUniqueFilename(originalName) {
  const timestamp = Date.now()
  const randomString = Math.random().toString(36).substring(2, 8)
  const extension = originalName.split('.').pop()
  const baseName = originalName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_')
  return `${baseName}_${timestamp}_${randomString}.${extension}`
}

/**
 * Upload a file to Cloudinary with progress tracking
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'characters/arthur')
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<object>} - { url, filename, path, publicId }
 */
export function uploadFile(file, folder, onProgress) {
  return new Promise((resolve, reject) => {
    // Validate configuration
    const validation = validateCloudinaryConfig()
    if (!validation.isValid) {
      reject(new Error(
        `Cloudinary configuration missing: ${validation.missing.join(', ')}. ` +
        'Please set these in your .env file and restart the dev server.'
      ))
      return
    }

    const filename = generateUniqueFilename(file.name)
    const fullPath = `${folder}/${filename}`

    // Create form data for upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', cloudinaryConfig.uploadPreset)
    formData.append('folder', folder)
    
    // Use XHR for progress tracking
    const xhr = new XMLHttpRequest()

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100)
          onProgress(progress)
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve({
            url: response.secure_url,
            filename: response.original_filename,
            path: fullPath,
            publicId: response.public_id,
          })
        } catch (error) {
          reject(new Error('Failed to parse Cloudinary response'))
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText)
          const errorMessage = errorResponse.error?.message || `Upload failed with status ${xhr.status}`
          reject(new Error(errorMessage))
        } catch {
          reject(new Error(
            `Upload failed with status ${xhr.status}. ` +
            `Check that upload preset "${cloudinaryConfig.uploadPreset}" exists in Cloudinary.`
          ))
        }
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    // Start upload
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`)
    xhr.send(formData)
  })
}

/**
 * Upload an image for a specific entity type
 * @param {File} file - The image file
 * @param {string} entityType - The entity type (characters, locations, events, shots)
 * @param {string} entityId - The entity ID (optional, for organization)
 * @param {function} onProgress - Progress callback
 * @returns {Promise<object>}
 */
export function uploadEntityImage(file, entityType, entityId, onProgress) {
  const folder = entityId 
    ? `images/${entityType}/${entityId}`
    : `images/${entityType}`
  return uploadFile(file, folder, onProgress)
}

/**
 * Delete a file from Cloudinary via server API
 * @param {string} publicId - The Cloudinary public ID
 * @returns {Promise<object>} - { success: boolean, result?: string, error?: string }
 */
export async function deleteFile(publicId) {
  if (!publicId) {
    console.warn('deleteFile called without publicId')
    return { success: false, error: 'No publicId provided' }
  }

  try {
    const response = await fetch('/api/cloudinary/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicId }),
    })

    const result = await response.json()
    
    if (response.ok) {
      console.log('Cloudinary image deleted:', publicId)
      return { success: true, result: result.result }
    } else {
      console.warn('Cloudinary delete failed:', result.error || result.message)
      return { success: false, error: result.error || result.message }
    }
  } catch (error) {
    console.error('Cloudinary delete request failed:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete a file by its URL (extracts publicId from Cloudinary URL)
 * @param {string} url - The Cloudinary URL
 * @returns {Promise<object>}
 */
export async function deleteFileByUrl(url) {
  if (!url) {
    return { success: false, error: 'No URL provided' }
  }

  const publicId = extractPublicIdFromUrl(url)
  if (!publicId) {
    console.warn('Could not extract publicId from URL:', url)
    return { success: false, error: 'Could not extract publicId from URL' }
  }

  return deleteFile(publicId)
}

/**
 * Extract Cloudinary public ID from a URL
 * URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{version}/{public_id}.{format}
 * @param {string} url - Cloudinary URL
 * @returns {string|null}
 */
export function extractPublicIdFromUrl(url) {
  if (!url || typeof url !== 'string') return null
  
  try {
    // Match Cloudinary URL pattern
    const match = url.match(/\/upload\/(?:v\d+\/)?(.+)\.[^.]+$/)
    if (match && match[1]) {
      return match[1]
    }
    return null
  } catch {
    return null
  }
}

/**
 * Delete multiple files from Cloudinary
 * @param {string[]} publicIds - Array of Cloudinary public IDs
 * @returns {Promise<object>}
 */
export async function deleteFiles(publicIds) {
  if (!publicIds || publicIds.length === 0) {
    return { results: [], errors: [] }
  }

  // Filter out any null/undefined values
  const validIds = publicIds.filter(id => id)
  
  if (validIds.length === 0) {
    return { results: [], errors: [] }
  }

  try {
    const response = await fetch('/api/cloudinary/delete-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicIds: validIds }),
    })

    const result = await response.json()
    
    if (response.ok) {
      console.log(`Cloudinary batch delete: ${result.results?.length || 0} succeeded`)
      return result
    } else {
      console.warn('Cloudinary batch delete failed:', result.error)
      return { results: [], errors: validIds.map(id => ({ publicId: id, error: result.error })) }
    }
  } catch (error) {
    console.error('Cloudinary batch delete request failed:', error)
    return { results: [], errors: validIds.map(id => ({ publicId: id, error: error.message })) }
  }
}

/**
 * Get URL for a Cloudinary image
 * @param {string} publicId - The Cloudinary public ID
 * @returns {string}
 */
export function getFileUrl(publicId) {
  return `https://res.cloudinary.com/${cloudinaryConfig.cloudName}/image/upload/${publicId}`
}

/**
 * List files - Not directly supported in client-side Cloudinary
 * Returns empty array as placeholder
 * @param {string} path - The path (unused)
 * @returns {Promise<array>}
 */
export async function listFiles(path) {
  console.log('List files requested for:', path)
  console.log('Note: File listing requires Cloudinary Admin API (server-side)')
  return []
}

/**
 * List files with URLs - Not directly supported in client-side Cloudinary
 * Returns empty array as placeholder
 * @param {string} path - The path (unused)
 * @returns {Promise<array>}
 */
export async function listFilesWithUrls(path) {
  console.log('List files with URLs requested for:', path)
  return []
}

// Supported image types
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]

// Max file size (10MB)
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/**
 * Validate an image file
 * @param {File} file - The file to validate
 * @returns {object} - { valid: boolean, error?: string }
 */
export function validateImageFile(file) {
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Unsupported file type. Please upload JPEG, PNG, GIF, or WebP.',
    }
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'File is too large. Maximum size is 10MB.',
    }
  }
  
  return { valid: true }
}
